import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WsmanagerService } from 'src/app/services/wsmanager.service';
import { ConvCode } from 'src/app/classes/conv/Conv';
import { ConvDataDefinition } from 'src/app/classes/conv/ConvDataDefinition';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { WidgetsmanagerService } from 'src/app/services/widgetsmanager.service';
import { forkJoin, Subscription } from 'rxjs';
import { HttpStatusCodesEnum } from 'src/app/utils/httpStatusCodesEnum';
import { City } from 'src/app/classes';
import { EntityTypeEnum, ITranslation } from 'src/app/classes/ITranslation';
import { SettingsmanagerService } from 'src/app/services/settingsmanager.service';

@Component({
  selector: 'app-conv-detail',
  templateUrl: './conv-detail.component.html',
  styleUrls: ['./conv-detail.component.scss']
})
export class ConvDetailComponent implements OnInit {
  @Input('conv')
  public conv: ConvCode;

  @Input('closeEvent')
  public closeEvent: EventEmitter<any>;

  @Output('convUpdated')
  private convUpdated: EventEmitter<ConvCode> = new EventEmitter<ConvCode>();

  @Input('cities')
  public cities: City[];

  @Input('allDataDef')
  public allDataDef: ConvDataDefinition[];

  public displayName = '';
  public isEditMode = true;
  public convForm: FormGroup;
  public validation_messages: any;
  public loading = false;
  public mexSuccess;
  public mexError;

  //supporto traduzioni
  // supporto modale tranduzioni
  public showTranslationsModal = false;
  public translations: ITranslation[] = [];
  public subscriptions: Subscription[] = [];
  public entityType: EntityTypeEnum = EntityTypeEnum.Convention;
  public entityId = -1;

  constructor(
    private formBuilder: FormBuilder,
    private wsManager: WsmanagerService,
    private translate: TranslateService,
    private widgetsManager: WidgetsmanagerService,
    private settingsManager: SettingsmanagerService
  ) {}

  ngOnInit() {
    // init form
    if (!this.allDataDef) {
      this.wsManager.fetchConvDataDefinitions().subscribe(
        (res) => {
          this.allDataDef = res;
        },
        (err) => {
          this.widgetsManager.showToastMessage('danger', 'Error while retrieving conv data definitions info');
        }
      );
    }

    if (!this.conv) {
      this.conv = new ConvCode();
      this.isEditMode = false;
    }

    if (this.conv && this.conv.id) {
      this.entityId = this.conv.id;
    }
    this.convForm = this.formBuilder.group({
      id: [this.conv.id, [Validators.nullValidator]],
      code: [this.conv.code, [Validators.required]],
      title: [this.conv.title, [Validators.required]],
      description: [this.conv.description, [Validators.required]],
      enabled: [this.conv.enabled, [Validators.required]],
      convDataDefinitions: [this.conv.convDataDefinitions, [Validators.nullValidator]],
      timeslots: [this.conv.timeslots, []],
      cityId: [this.conv ? this.conv.cityId : null, []]
    });
    if (this.isEditMode) {
      this.convForm.addControl('id', new FormControl(this.conv.id));
    }

    // init mex validazione
    this.validation_messages = {
      code: [{ type: 'required', message: 'Field is required.' }],
      enabled: [{ type: 'required', message: 'Field is required.' }],
      title: [{ type: 'required', message: 'Field is required.' }],
      description: [{ type: 'required', message: 'Field is required.' }]
    };

    this.mexSuccess = this.translate.instant(this.isEditMode ? 'Convention modified' : 'Convention added');
    this.mexError = this.translate.instant(this.isEditMode ? 'Error modifying convention' : 'Error adding convention');

    this.loadTranslationsByEntity();
  }

  update() {
    this.loading = true;

    forkJoin(this.wsManager.fetchConvDataDefinitions(), this.wsManager.getConvCode(this.conv.code)).subscribe(
      (res) => {
        this.allDataDef = res[0];
        this.conv = res[1];
        this.convForm.patchValue({
          id: this.conv.id,
          code: this.conv.code,
          title: this.conv.title,
          description: this.conv.description,
          enabled: this.conv.enabled,
          convDataDefinitions: this.conv.convDataDefinitions,
          timeslots: this.conv.timeslots
        });
        this.convUpdated.emit(this.conv);
        this.loading = false;
      },
      (err) => {
        let actiondMsg = 'Error';
        if (err.status === HttpStatusCodesEnum.CONFLICT && err.error && err.error.overlappingTimeslot) {
          actiondMsg = this.translate.instant('overlappingTimeslot');
        }
        this.widgetsManager.showToastMessage('danger', actiondMsg);
        this.loading = false;
        this.closeEvent.emit();
      }
    );
  }

  compareWithDataDefs(o1: ConvDataDefinition, o2: ConvDataDefinition) {
    return o1.id === o2.id;
  }

  close() {
    if (this.closeEvent) {
      this.closeEvent.emit();
    }
  }

  loadTranslationsByEntity() {
    this.subscriptions.push(
      this.wsManager.fetchTranslations(this.entityType, this.entityId).subscribe(
        (response) => {
          console.log('@@@@@ translations by entity');
          this.translations = response;
        },
        (err) => {
          console.error('Error fetching translations:', err);
        },
        () => {
          console.log('Completed fetching translations.');
        }
      )
    );
  }

  refreshContent(refreshEvent: { method: 'CREATE' | 'DELETE' | 'EDIT'; translation: ITranslation }) {
    const lang = this.settingsManager.getLanguageSetting();
    const { method, translation } = refreshEvent;
    switch (method) {
      case 'CREATE':
        this.translations.push(translation);
        this.translations = [...this.translations];
        break;
      case 'DELETE':
        this.translations = this.translations.filter((t) => t.id !== translation.id);
        break;
      case 'EDIT': {
        const index = this.translations.findIndex((t) => t.id === translation.id);
        this.translations[index] = translation;
        break;
      }
    }
    if (translation.locale === lang) {
      if (translation.value === '') {
        this.convForm.get(translation.field).setValue(this.conv[translation.field]);
      } else {
        this.convForm.get(translation.field).setValue(translation.value);
      }
    }
  }

  changeEntityLanguage(language: string) {
    this.settingsManager.setLanguageSetting(language);
    const fieldsToReset = ['title', 'description'];
    fieldsToReset.forEach((field) => {
      if (this.convForm.get(field)) {
        this.convForm.get(field).setValue(this.conv[field]);
      }
    });
    const localizedtranslations = this.translations.filter((t) => t.locale === language);
    localizedtranslations.forEach((lt) => {
      this.convForm.get(lt.field).setValue(lt.value);
    });
  }

  onSubmit(values: ConvCode) {
    this.loading = true;
    // If no data definitions are provided, then they are an empty list
    if (!values.convDataDefinitions) values.convDataDefinitions = [];

    if (typeof values.cityId != 'number') values.cityId = null;

    const obs = this.isEditMode ? this.wsManager.editConv(values) : this.wsManager.newConv(values);

    obs.subscribe(
      (data) => {
        this.conv = data;
        this.isEditMode = true;
        this.update();
        this.widgetsManager.showToastMessage('success', this.mexSuccess);
      },
      (err) => {
        if (err.status === HttpStatusCodesEnum.CONFLICT && err.error && err.error.overlappingTimeslot) {
          this.mexError = this.translate.instant('overlappingTimeslot');
        }
        this.loading = false;
        this.widgetsManager.showToastMessage('danger', this.mexError);
      }
    );
  }

  saveTimeslots(timeslots) {
    this.convForm.controls['timeslots'].setValue(timeslots);
  }
}
